import React, { useState } from 'react';
import {
  Calendar,
  DollarSign,
  CheckCircle2,
  Clock,
  TrendingUp,
  Users,
  Shield,
  Zap,
  ChevronDown,
  ChevronUp,
  Target,
  AlertCircle,
  Activity,
  BarChart3,
  Award,
  Settings
} from 'lucide-react';

interface MetricDetail {
  metric: string;
  baseline: string;
  target: string;
  measurement_frequency: string;
  data_source: string;
}

interface KPITracking {
  efficiency_metrics?: {
    time_savings?: MetricDetail;
    throughput?: MetricDetail;
    cycle_time?: MetricDetail;
  };
  adoption_metrics?: {
    automation_rate?: MetricDetail;
    user_adoption?: MetricDetail;
    exception_rate?: MetricDetail;
    training_completion?: MetricDetail;
  };
  quality_metrics?: {
    error_reduction?: MetricDetail;
    accuracy_rate?: MetricDetail;
    rework_rate?: MetricDetail;
    customer_satisfaction?: MetricDetail;
    audit_findings?: MetricDetail;
  };
  financial_metrics?: {
    cost_savings?: MetricDetail;
    roi?: MetricDetail;
    headcount_avoidance?: MetricDetail;
    licensing_cost_comparison?: MetricDetail;
  };
  technical_metrics?: {
    uptime?: MetricDetail;
    api_error_rate?: MetricDetail;
    response_time?: MetricDetail;
    capacity_utilization?: MetricDetail;
  };
  measurement_approach?: {
    dashboard_link?: string;
    baseline_period?: string;
    monitoring_start?: string;
    review_frequency?: string;
    stakeholder_reviews?: string;
    benchmark_comparison?: string;
  };
}

interface Phase {
  phase_number: number;
  phase_name: string;
  timeline: string;
  value_delivered?: string;
  what_gets_automated?: string[];
  what_stays_manual?: string[];
  what_changes?: string[];
  manual_steps_remaining?: string;
  approvals_required?: Array<{
    approval: string;
    approver: string;
    typical_time: string;
    complexity: string;
    notes?: string;
  }>;
  investment?: any;
  benefits?: any;
  roi?: any;
  technical_requirements?: string[];
  success_criteria?: string[];
  kpi_tracking?: KPITracking;
  risks_and_mitigation?: Array<{
    risk: string;
    mitigation: string;
    impact: string;
  }>;
  recommendation?: string;
  when_needed?: string;
  next_step_trigger?: string;
}

interface PhasedRoadmapProps {
  phasedPlan: {
    total_phases: number;
    recommended_starting_phase: number;
    phase_1?: Phase;
    phase_2?: Phase;
    phase_3?: Phase;
  };
  recommendedPlatform?: string;
  recommendedApproach?: string;
}

const PhasedRoadmap: React.FC<PhasedRoadmapProps> = ({
  phasedPlan,
  recommendedPlatform,
  recommendedApproach
}) => {
  const [expandedPhases, setExpandedPhases] = useState<{[key: number]: boolean}>({
    1: true,
    2: false,
    3: false
  });

  const togglePhase = (phaseNum: number) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phaseNum]: !prev[phaseNum]
    }));
  };

  const getPhaseColor = (phaseNum: number) => {
    if (phaseNum === 1) return {
      bg: 'from-green-500 to-emerald-500',
      border: 'border-green-500',
      light: 'bg-green-50',
      text: 'text-green-700',
      badge: 'bg-green-500'
    };
    if (phaseNum === 2) return {
      bg: 'from-blue-500 to-cyan-500',
      border: 'border-blue-500',
      light: 'bg-blue-50',
      text: 'text-blue-700',
      badge: 'bg-blue-500'
    };
    return {
      bg: 'from-purple-500 to-pink-500',
      border: 'border-purple-500',
      light: 'bg-purple-50',
      text: 'text-purple-700',
      badge: 'bg-purple-500'
    };
  };

  const renderPhase = (phase: Phase | undefined, phaseNum: number) => {
    if (!phase) return null;

    const colors = getPhaseColor(phaseNum);
    const isExpanded = expandedPhases[phaseNum];

    return (
      <div className="relative">
        {phaseNum > 1 && (
          <div className="absolute left-6 -top-6 w-0.5 h-6 bg-gradient-to-b from-gray-300 to-gray-400"></div>
        )}

        <div className={`border-2 ${colors.border} rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow`}>
          <div
            className={`bg-gradient-to-r ${colors.bg} text-white p-5 cursor-pointer`}
            onClick={() => togglePhase(phaseNum)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center text-2xl font-bold">
                  {phaseNum}
                </div>
                <div>
                  <h3 className="text-xl font-bold">{phase.phase_name}</h3>
                  <div className="flex items-center gap-4 mt-2 text-sm opacity-90">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{phase.timeline}</span>
                    </div>
                    {phase.value_delivered && (
                      <div className="flex items-center gap-1">
                        <Target className="w-4 h-4" />
                        <span>{phase.value_delivered} Value</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <button className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                {isExpanded ? (
                  <ChevronUp className="w-6 h-6" />
                ) : (
                  <ChevronDown className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {isExpanded && (
            <div className="bg-white p-6 space-y-6">
              {phase.what_gets_automated && phase.what_gets_automated.length > 0 && (
                <div className={`${colors.light} border-2 ${colors.border} rounded-lg p-4`}>
                  <h4 className={`text-sm font-bold ${colors.text} mb-3 flex items-center gap-2`}>
                    <CheckCircle2 className="w-4 h-4" />
                    What Gets Automated
                  </h4>
                  <ul className="space-y-2">
                    {phase.what_gets_automated.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-green-600 mt-0.5">✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {phase.what_changes && phase.what_changes.length > 0 && (
                <div className={`${colors.light} border-2 ${colors.border} rounded-lg p-4`}>
                  <h4 className={`text-sm font-bold ${colors.text} mb-3 flex items-center gap-2`}>
                    <Zap className="w-4 h-4" />
                    What Changes in This Phase
                  </h4>
                  <ul className="space-y-2">
                    {phase.what_changes.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-blue-600 mt-0.5">→</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {phase.what_stays_manual && phase.what_stays_manual.length > 0 && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-amber-800 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    What Stays Manual (For Now)
                  </h4>
                  <ul className="space-y-2">
                    {phase.what_stays_manual.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-amber-600 mt-0.5">⏱</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {phase.approvals_required && phase.approvals_required.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-purple-600" />
                    Approvals Required
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="px-4 py-2 text-left border border-gray-300">Approval</th>
                          <th className="px-4 py-2 text-left border border-gray-300">Approver</th>
                          <th className="px-4 py-2 text-left border border-gray-300">Timeline</th>
                          <th className="px-4 py-2 text-left border border-gray-300">Complexity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {phase.approvals_required.map((approval, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 border border-gray-300 font-medium">{approval.approval}</td>
                            <td className="px-4 py-2 border border-gray-300">{approval.approver}</td>
                            <td className="px-4 py-2 border border-gray-300">{approval.typical_time}</td>
                            <td className="px-4 py-2 border border-gray-300">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                approval.complexity === 'Low' ? 'bg-green-100 text-green-700' :
                                approval.complexity === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {approval.complexity}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {phase.approvals_required[0]?.notes && (
                    <p className="text-xs text-gray-600 mt-2 italic">💡 {phase.approvals_required[0].notes}</p>
                  )}
                </div>
              )}

              {phase.investment && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-green-800 mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Investment Required
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {phase.investment.licensing && (
                      <div className="bg-white rounded p-2 border border-green-200">
                        <p className="text-xs text-gray-600 mb-1">Licensing</p>
                        <p className="text-sm font-semibold text-gray-900">{phase.investment.licensing}</p>
                      </div>
                    )}
                    {phase.investment.setup_effort && (
                      <div className="bg-white rounded p-2 border border-green-200">
                        <p className="text-xs text-gray-600 mb-1">Setup Effort</p>
                        <p className="text-sm font-semibold text-gray-900">{phase.investment.setup_effort}</p>
                      </div>
                    )}
                    {phase.investment.setup_cost && (
                      <div className="bg-white rounded p-2 border border-green-200">
                        <p className="text-xs text-gray-600 mb-1">Setup Cost</p>
                        <p className="text-sm font-semibold text-gray-900">{phase.investment.setup_cost}</p>
                      </div>
                    )}
                    {phase.investment.total_first_year && (
                      <div className="bg-green-500 text-white rounded p-2 border-2 border-green-600">
                        <p className="text-xs opacity-90 mb-1">Total Year 1</p>
                        <p className="text-sm font-bold">{phase.investment.total_first_year}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {phase.benefits && (
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-300 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Benefits
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(phase.benefits).map(([key, value]) => (
                      <div key={key} className="bg-white rounded p-3 border border-blue-200">
                        <p className="text-xs text-gray-600 mb-1 capitalize">{key.replace(/_/g, ' ')}</p>
                        <p className="text-sm font-semibold text-gray-900">{value as string}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {phase.roi && (
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg p-4">
                  <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Return on Investment (ROI)
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {phase.roi.payback_period && (
                      <div className="bg-white/20 backdrop-blur rounded p-2">
                        <p className="text-xs opacity-90 mb-1">Payback Period</p>
                        <p className="text-lg font-bold">{phase.roi.payback_period}</p>
                      </div>
                    )}
                    {phase.roi.roi_calculation && (
                      <div className="bg-white/20 backdrop-blur rounded p-2">
                        <p className="text-xs opacity-90 mb-1">ROI Calculation</p>
                        <p className="text-sm font-semibold">{phase.roi.roi_calculation}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {phase.technical_requirements && phase.technical_requirements.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-indigo-600" />
                    Technical Requirements
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <ul className="space-y-1.5">
                      {phase.technical_requirements.map((req, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="text-indigo-600 mt-0.5">•</span>
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {phase.success_criteria && phase.success_criteria.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-green-600" />
                    Success Criteria
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <ul className="space-y-1.5">
                      {phase.success_criteria.map((criteria, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{criteria}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* KPI Tracking Section */}
              {phase.kpi_tracking && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-5">
                  <h4 className="text-base font-bold text-blue-900 mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                    Comprehensive KPI Tracking & Success Metrics
                  </h4>

                  {/* Efficiency Metrics */}
                  {phase.kpi_tracking.efficiency_metrics && (
                    <div className="mb-4">
                      <h5 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                        <Zap className="w-4 h-4 text-amber-600" />
                        Efficiency Metrics
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(phase.kpi_tracking.efficiency_metrics).map(([key, metric]) => (
                          <div key={key} className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                            <div className="text-xs font-bold text-blue-900 mb-2">{metric.metric}</div>
                            <div className="space-y-1 text-xs">
                              <div><span className="font-semibold text-gray-600">Baseline:</span> <span className="text-gray-800">{metric.baseline}</span></div>
                              <div><span className="font-semibold text-gray-600">Target:</span> <span className="text-green-700 font-semibold">{metric.target}</span></div>
                              <div className="pt-1 border-t border-gray-200">
                                <div className="text-[10px] text-gray-500">📊 {metric.measurement_frequency}</div>
                                <div className="text-[10px] text-gray-500">📁 {metric.data_source}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Adoption Metrics */}
                  {phase.kpi_tracking.adoption_metrics && (
                    <div className="mb-4">
                      <h5 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                        <Users className="w-4 h-4 text-purple-600" />
                        Adoption Metrics
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(phase.kpi_tracking.adoption_metrics).map(([key, metric]) => (
                          <div key={key} className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                            <div className="text-xs font-bold text-purple-900 mb-2">{metric.metric}</div>
                            <div className="space-y-1 text-xs">
                              <div><span className="font-semibold text-gray-600">Baseline:</span> <span className="text-gray-800">{metric.baseline}</span></div>
                              <div><span className="font-semibold text-gray-600">Target:</span> <span className="text-green-700 font-semibold">{metric.target}</span></div>
                              <div className="pt-1 border-t border-gray-200">
                                <div className="text-[10px] text-gray-500">📊 {metric.measurement_frequency}</div>
                                <div className="text-[10px] text-gray-500">📁 {metric.data_source}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quality Metrics */}
                  {phase.kpi_tracking.quality_metrics && (
                    <div className="mb-4">
                      <h5 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                        <Award className="w-4 h-4 text-green-600" />
                        Quality Metrics
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(phase.kpi_tracking.quality_metrics).map(([key, metric]) => (
                          <div key={key} className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                            <div className="text-xs font-bold text-green-900 mb-2">{metric.metric}</div>
                            <div className="space-y-1 text-xs">
                              <div><span className="font-semibold text-gray-600">Baseline:</span> <span className="text-gray-800">{metric.baseline}</span></div>
                              <div><span className="font-semibold text-gray-600">Target:</span> <span className="text-green-700 font-semibold">{metric.target}</span></div>
                              <div className="pt-1 border-t border-gray-200">
                                <div className="text-[10px] text-gray-500">📊 {metric.measurement_frequency}</div>
                                <div className="text-[10px] text-gray-500">📁 {metric.data_source}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Financial Metrics */}
                  {phase.kpi_tracking.financial_metrics && (
                    <div className="mb-4">
                      <h5 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-emerald-600" />
                        Financial Metrics
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(phase.kpi_tracking.financial_metrics).map(([key, metric]) => (
                          <div key={key} className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                            <div className="text-xs font-bold text-emerald-900 mb-2">{metric.metric}</div>
                            <div className="space-y-1 text-xs">
                              <div><span className="font-semibold text-gray-600">Baseline:</span> <span className="text-gray-800">{metric.baseline}</span></div>
                              <div><span className="font-semibold text-gray-600">Target:</span> <span className="text-green-700 font-semibold">{metric.target}</span></div>
                              <div className="pt-1 border-t border-gray-200">
                                <div className="text-[10px] text-gray-500">📊 {metric.measurement_frequency}</div>
                                <div className="text-[10px] text-gray-500">📁 {metric.data_source}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Technical Metrics */}
                  {phase.kpi_tracking.technical_metrics && (
                    <div className="mb-4">
                      <h5 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                        <Settings className="w-4 h-4 text-gray-600" />
                        Technical Metrics
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(phase.kpi_tracking.technical_metrics).map(([key, metric]) => (
                          <div key={key} className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                            <div className="text-xs font-bold text-gray-900 mb-2">{metric.metric}</div>
                            <div className="space-y-1 text-xs">
                              <div><span className="font-semibold text-gray-600">Baseline:</span> <span className="text-gray-800">{metric.baseline}</span></div>
                              <div><span className="font-semibold text-gray-600">Target:</span> <span className="text-green-700 font-semibold">{metric.target}</span></div>
                              <div className="pt-1 border-t border-gray-200">
                                <div className="text-[10px] text-gray-500">📊 {metric.measurement_frequency}</div>
                                <div className="text-[10px] text-gray-500">📁 {metric.data_source}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Measurement Approach */}
                  {phase.kpi_tracking.measurement_approach && (
                    <div className="bg-blue-100 rounded-lg p-4 border border-blue-300">
                      <h5 className="text-sm font-bold text-blue-900 mb-3 flex items-center gap-2">
                        <Activity className="w-4 h-4" />
                        Measurement Approach
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {phase.kpi_tracking.measurement_approach.baseline_period && (
                          <div className="text-xs">
                            <span className="font-semibold text-blue-800">Baseline Period:</span>
                            <span className="ml-2 text-gray-800">{phase.kpi_tracking.measurement_approach.baseline_period}</span>
                          </div>
                        )}
                        {phase.kpi_tracking.measurement_approach.monitoring_start && (
                          <div className="text-xs">
                            <span className="font-semibold text-blue-800">Monitoring Start:</span>
                            <span className="ml-2 text-gray-800">{phase.kpi_tracking.measurement_approach.monitoring_start}</span>
                          </div>
                        )}
                        {phase.kpi_tracking.measurement_approach.review_frequency && (
                          <div className="text-xs">
                            <span className="font-semibold text-blue-800">Review Frequency:</span>
                            <span className="ml-2 text-gray-800">{phase.kpi_tracking.measurement_approach.review_frequency}</span>
                          </div>
                        )}
                        {phase.kpi_tracking.measurement_approach.stakeholder_reviews && (
                          <div className="text-xs">
                            <span className="font-semibold text-blue-800">Stakeholder Reviews:</span>
                            <span className="ml-2 text-gray-800">{phase.kpi_tracking.measurement_approach.stakeholder_reviews}</span>
                          </div>
                        )}
                        {phase.kpi_tracking.measurement_approach.benchmark_comparison && (
                          <div className="text-xs">
                            <span className="font-semibold text-blue-800">Benchmark Comparison:</span>
                            <span className="ml-2 text-gray-800">{phase.kpi_tracking.measurement_approach.benchmark_comparison}</span>
                          </div>
                        )}
                        {phase.kpi_tracking.measurement_approach.dashboard_link && (
                          <div className="text-xs md:col-span-2">
                            <span className="font-semibold text-blue-800">Dashboard:</span>
                            <span className="ml-2 px-2 py-1 bg-blue-200 text-blue-800 rounded-md font-medium">
                              {phase.kpi_tracking.measurement_approach.dashboard_link}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {phase.risks_and_mitigation && phase.risks_and_mitigation.length > 0 && (
                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-red-800 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Risks & Mitigation
                  </h4>
                  <div className="space-y-2">
                    {phase.risks_and_mitigation.map((item, idx) => (
                      <div key={idx} className="bg-white rounded p-3 border border-red-200">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                            item.impact === 'High' ? 'bg-red-100 text-red-700' :
                            item.impact === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {item.impact} Impact
                          </span>
                        </div>
                        <p className="text-sm text-gray-800 mb-1"><strong>Risk:</strong> {item.risk}</p>
                        <p className="text-sm text-blue-700">💡 <strong>Mitigation:</strong> {item.mitigation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {phase.next_step_trigger && (
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-yellow-900 mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Next Step
                  </h4>
                  <p className="text-sm text-gray-700">{phase.next_step_trigger}</p>
                </div>
              )}

              {phase.recommendation && (
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-4">
                  <p className="text-sm font-semibold">💡 {phase.recommendation}</p>
                </div>
              )}

              {phase.when_needed && (
                <div className="bg-gray-100 border-2 border-gray-300 rounded-lg p-4">
                  <h4 className="text-sm font-bold text-gray-800 mb-2">When is this phase needed?</h4>
                  <p className="text-sm text-gray-700">{phase.when_needed}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl p-6 shadow-lg">
        <h2 className="text-2xl font-bold mb-4">Phased Implementation Roadmap</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white/20 backdrop-blur rounded-lg p-3">
            <p className="opacity-90 mb-1">Total Phases</p>
            <p className="text-2xl font-bold">{phasedPlan.total_phases}</p>
          </div>
          <div className="bg-white/20 backdrop-blur rounded-lg p-3">
            <p className="opacity-90 mb-1">Recommended Platform</p>
            <p className="text-lg font-semibold">{recommendedPlatform || 'Not specified'}</p>
          </div>
          <div className="bg-white/20 backdrop-blur rounded-lg p-3">
            <p className="opacity-90 mb-1">Recommended Start</p>
            <p className="text-lg font-semibold">Phase {phasedPlan.recommended_starting_phase}</p>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {renderPhase(phasedPlan.phase_1, 1)}
        {renderPhase(phasedPlan.phase_2, 2)}
        {renderPhase(phasedPlan.phase_3, 3)}
      </div>
    </div>
  );
};

export default PhasedRoadmap;
